import React, { useState } from 'react';
import { IoStar, IoStarOutline } from 'react-icons/io5';

interface StarRatingProps {
  messageId: number;
  existingRating?: number;
  existingComment?: string;
  colors: any;
  onSubmit: (messageId: number, rating: number, comment: string) => Promise<void>;
}

const StarRating: React.FC<StarRatingProps> = ({
  messageId, existingRating, existingComment, colors, onSubmit,
}) => {
  const [hoverRating, setHoverRating] = useState(0);
  const [selectedRating, setSelectedRating] = useState(existingRating || 0);
  const [comment, setComment] = useState(existingComment || '');
  const [showComment, setShowComment] = useState(false);
  const [submitted, setSubmitted] = useState(!!existingRating);
  const [submitting, setSubmitting] = useState(false);

  const handleStarClick = (rating: number) => {
    if (submitted) return;
    setSelectedRating(rating);
    setShowComment(true);
  };

  const handleSubmit = async () => {
    if (selectedRating === 0 || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(messageId, selectedRating, comment);
      setSubmitted(true);
      setShowComment(false);
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setSelectedRating(0);
    setComment('');
    setShowComment(false);
  };

  const displayRating = hoverRating || selectedRating;

  return (
    <div className="flex flex-col gap-1 mt-1">
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onMouseEnter={() => !submitted && setHoverRating(star)}
            onMouseLeave={() => setHoverRating(0)}
            onClick={() => handleStarClick(star)}
            className={`p-0.5 transition-transform ${submitted ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
            disabled={submitted}
          >
            {star <= displayRating ? (
              React.createElement(IoStar as any, {
                size: 16,
                color: colors.primary,
              })
            ) : (
              React.createElement(IoStarOutline as any, {
                size: 16,
                color: colors.grayScale400,
              })
            )}
          </button>
        ))}
        {submitted && (
          <span className="text-xs ml-1" style={{ color: colors.grayScale500 }}>
            {existingComment || comment ? 'Feedback submitted' : `Rated ${selectedRating}/5`}
          </span>
        )}
      </div>

      {showComment && !submitted && (
        <div className="flex flex-col gap-2 mt-1">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Any additional feedback? (optional)"
            rows={2}
            className="text-xs rounded-lg px-3 py-2 resize-none outline-none"
            style={{
              backgroundColor: colors.backgroundColor,
              border: `1px solid ${colors.borderColor}`,
              color: colors.textColor,
            }}
          />
          <div className="flex items-center gap-2">
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="text-xs px-3 py-1 rounded-lg font-medium"
              style={{
                backgroundColor: colors.primary,
                color: colors.white,
                opacity: submitting ? 0.6 : 1,
              }}
            >
              {submitting ? 'Sending...' : 'Submit'}
            </button>
            <button
              onClick={handleCancel}
              className="text-xs px-3 py-1 rounded-lg"
              style={{
                backgroundColor: colors.backgroundColor,
                border: `1px solid ${colors.borderColor}`,
                color: colors.grayScale500,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StarRating;
